import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useFocusTrap } from '../hooks';
import { useToast } from '../context';
import './ReportModal.css';

const REPORT_TYPES = [
  { value: 'bug', icon: 'fa-bug', labelKey: 'report.types.bug' },
  { value: 'wrong_point', icon: 'fa-map-marker-alt', labelKey: 'report.types.wrongPoint' },
  { value: 'wrong_image', icon: 'fa-image', labelKey: 'report.types.wrongImage' },
  { value: 'inappropriate', icon: 'fa-exclamation-triangle', labelKey: 'report.types.inappropriate' },
  { value: 'suggestion', icon: 'fa-lightbulb', labelKey: 'report.types.suggestion' }
];

const ReportModal = ({ onClose, context = {} }) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [type, setType] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Focus trap for accessibility
  const modalRef = useFocusTrap(true, {
    onEscape: onClose,
    autoFocus: true,
    restoreFocus: true
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    // Validation
    if (!type) {
      setError(t('report.errors.selectType', 'Please select a report type'));
      return;
    }
    if (title.length < 5) {
      setError(t('report.errors.titleTooShort', 'Title must be at least 5 characters'));
      return;
    }
    if (description.length < 10) {
      setError(t('report.errors.descriptionTooShort', 'Description must be at least 10 characters'));
      return;
    }

    setSubmitting(true);

    try {
      const response = await fetch('/api/reports', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          type,
          title,
          description,
          pointId: context.pointId || null,
          mapId: context.mapId || null,
          sessionId: context.sessionId || null
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || t('report.errors.submitFailed', 'Failed to submit report'));
      }

      toast.success(t('report.success', 'Report submitted successfully!'));
      onClose();
    } catch (err) {
      setError(err.message);
      toast.error(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="report-overlay" role="presentation">
      <div
        className="report-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="report-title"
        ref={modalRef}
        tabIndex={-1}
      >
        {/* Header */}
        <div className="report-header">
          <h2 id="report-title">
            <i className="fas fa-flag"></i>
            {t('report.title', 'Submit a Report')}
          </h2>
          <button
            className="report-close"
            onClick={onClose}
            aria-label={t('report.close', 'Close')}
          >
            <i className="fas fa-times"></i>
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="report-form">
          {/* Report Type Selection */}
          <div className="report-field">
            <label className="report-label">
              {t('report.typeLabel', 'What are you reporting?')}
            </label>
            <div className="report-types">
              {REPORT_TYPES.map(rt => (
                <button
                  key={rt.value}
                  type="button"
                  className={`report-type-btn ${type === rt.value ? 'selected' : ''}`}
                  onClick={() => setType(rt.value)}
                >
                  <i className={`fas ${rt.icon}`}></i>
                  <span>{t(rt.labelKey, rt.value)}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Title */}
          <div className="report-field">
            <label htmlFor="report-title-input" className="report-label">
              {t('report.titleLabel', 'Title')}
            </label>
            <input
              id="report-title-input"
              type="text"
              className="report-input"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={t('report.titlePlaceholder', 'Brief summary of the issue')}
              maxLength={200}
              required
            />
            <span className="report-char-count">{title.length}/200</span>
          </div>

          {/* Description */}
          <div className="report-field">
            <label htmlFor="report-description" className="report-label">
              {t('report.descriptionLabel', 'Description')}
            </label>
            <textarea
              id="report-description"
              className="report-textarea"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={t('report.descriptionPlaceholder', 'Please provide as much detail as possible...')}
              maxLength={2000}
              rows={5}
              required
            />
            <span className="report-char-count">{description.length}/2000</span>
          </div>

          {/* Context info (if any) */}
          {(context.mapName || context.sessionId) && (
            <div className="report-context">
              <span className="report-context-label">
                <i className="fas fa-info-circle"></i>
                {t('report.contextInfo', 'Context:')}
              </span>
              {context.mapName && (
                <span className="report-context-item">
                  <i className="fas fa-map"></i> {context.mapName}
                </span>
              )}
              {context.sessionId && (
                <span className="report-context-item">
                  <i className="fas fa-gamepad"></i> {t('report.currentGame', 'Current game')}
                </span>
              )}
            </div>
          )}

          {/* Error message */}
          {error && (
            <div className="report-error">
              <i className="fas fa-exclamation-circle"></i>
              {error}
            </div>
          )}

          {/* Footer */}
          <div className="report-footer">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onClose}
              disabled={submitting}
            >
              {t('report.cancel', 'Cancel')}
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={submitting || !type || title.length < 5 || description.length < 10}
            >
              {submitting ? (
                <>
                  <i className="fas fa-spinner fa-spin"></i>
                  {t('report.submitting', 'Submitting...')}
                </>
              ) : (
                <>
                  <i className="fas fa-paper-plane"></i>
                  {t('report.submit', 'Submit Report')}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ReportModal;
